import React, { useEffect } from 'react';
import { Images, User, Building, Camera, Check, Edit3, Star } from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import OnboardingImageSlotManager from './OnboardingImageSlotManager';
import { useOnboardingImages } from '../hooks/useOnboardingImages';

const OnboardingPhotosCard = ({ client, clientDataHook }) => {
	const { refreshData, isOnboarding, goToNextStep, clientImages, isCardEditMode, toggleCardEditMode } = clientDataHook;
	const cardName = 'photosInfo';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	// Use shared clientImages from wizard instead of creating new instance
	const { hasMinimumRequiredImages, getImagesByType } = clientImages;

	// Refresh data when component mounts
	useEffect(() => {
		if (isOnboarding && refreshData) {
			console.log('🟦 OnboardingPhotosCard - Component mounted, refreshing data');
			refreshData();
		}
	}, [isOnboarding]); // Remove refreshData from dependencies to prevent infinite loop

	const imageCategories = [
		{
			type: 'headshot',
			title: 'Headshots',
			icon: User,
			maxSlots: 3,
			description: 'Upload professional headshots for your profile. The primary image will be used as your main profile photo.',
			required: true,
		},
		{
			type: 'logo',
			title: 'Personal Logos',
			icon: Building,
			maxSlots: 3,
			description: 'Upload your personal branding logos.',
			required: true,
		},
		{
			type: 'broker_logo',
			title: 'Broker Logos',
			icon: Building,
			maxSlots: 2,
			description: 'Upload your brokerage logos and branding.',
			required: true,
		},
		{
			type: 'lifestyle',
			title: 'Lifestyle Photos',
			icon: Camera,
			maxSlots: 3,
			description: 'Upload lifestyle and marketing photos that represent you and your brand.',
			required: false,
		},
	];

	// Handle save for onboarding
	const handleOnboardingSave = async () => {
		// Check if minimum required images are uploaded
		if (!hasMinimumRequiredImages()) {
			alert('Please upload at least one headshot, one personal logo, and one broker logo to continue.');
			return false;
		}

		// No need to update any legacy fields - all photos are managed via user_images table
		// Trigger navigation to next step
		if (goToNextStep) {
			goToNextStep();
		}
		
		return true;
	};

	// Calculate upload progress
	const totalPossibleSlots = imageCategories.reduce((sum, cat) => sum + cat.maxSlots, 0);
	const totalUploadedImages = imageCategories.reduce((sum, cat) => {
		return sum + getImagesByType(cat.type).length;
	}, 0);
	const uploadProgress = Math.round((totalUploadedImages / totalPossibleSlots) * 100);

	return (
		<Card className='bg-white rounded-lg shadow-sm border'>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center justify-center gap-2 text-lg font-montserrat-medium text-gray-900'>
							<Images className='h-5 w-5' />
							Professional Photos
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Upload your professional photos organized by type. Each category supports multiple images with drag-and-drop functionality.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			{isOnboarding && (
				<Card.Header>
					<Card.Title className='flex items-center gap-2'>
						<Images className='h-5 w-5' />
						Professional Photos
					</Card.Title>
					<Card.Description>
						Upload your professional photos organized by type. Each category supports multiple images with drag-and-drop functionality.
					</Card.Description>
				</Card.Header>
			)}
			<Card.Content className='space-y-8'>
				{isEditing ? (
					<>
						{/* Progress indicator */}
						<div className='mb-6'>
							<div className='flex justify-between items-center mb-2'>
								<span className='text-sm font-medium text-gray-600'>
									Photo Upload Progress
								</span>
								<span className='text-sm font-medium text-gray-600'>
									{uploadProgress}% ({totalUploadedImages}/{totalPossibleSlots} slots filled)
								</span>
							</div>
							<div className='w-full bg-gray-200 rounded-full h-2'>
								<div
									className='bg-blue-600 h-2 rounded-full transition-all duration-300'
									style={{ width: `${uploadProgress}%` }}
								/>
							</div>
						</div>

						{/* Image categories */}
						{imageCategories.map((category) => (
							<div
								key={category.type}
								className='border-b border-gray-100 last:border-0 pb-6 last:pb-0'>
								<div className='flex items-center mb-3'>
									<category.icon className='w-5 h-5 text-gray-600 mr-2' />
									<h3 className='text-lg font-semibold text-gray-800'>
										{category.title}
									</h3>
									{category.required && (
										<span className='ml-2 text-sm text-red-500'>*</span>
									)}
									{category.maxSlots && (
										<span className='ml-2 text-sm text-gray-400'>
											(Max {category.maxSlots})
										</span>
									)}
								</div>

								<OnboardingImageSlotManager
									client={client}
									clientImages={clientImages}
									imageType={category.type}
									maxSlots={category.maxSlots}
									title={category.title}
									description={category.description}
									required={category.required}
									isProfileMode={!isOnboarding}
								/>
							</div>
						))}

						{/* Required fields note */}
						{isOnboarding && (
							<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
								<p className='text-sm text-blue-800'>
									<strong>Note:</strong> You must upload at least one headshot, one personal logo, and one broker logo to continue with onboarding. 
									You can always add more photos later from your profile.
								</p>
							</div>
						)}
					</>
				) : (
					/* View mode - simplified display */
					<div className='space-y-4'>
						{imageCategories.map((category) => {
							const images = getImagesByType(category.type);
							const maxThumbnails = 5;
							const displayImages = images.slice(0, maxThumbnails);
							const remainingCount = images.length - maxThumbnails;
							
							return (
								<div key={category.type} className='space-y-2'>
									<label className='text-sm font-semibold text-gray-800 flex items-center gap-2'>
										<category.icon className='h-4 w-4 text-gray-500' />
										{category.title}
									</label>
									<div className='flex items-start gap-2'>
										{images.length > 0 ? (
											<div className='flex gap-2 items-center'>
												{displayImages.map((image, index) => (
													<div key={image.id} className='relative'>
														<div className='w-12 h-12 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors flex items-center justify-center bg-gray-50'>
															<img
																src={image.imageUrl}
																alt={`${category.title} ${index + 1}`}
																className='w-full h-full object-cover'
															/>
														</div>
														{image.isPrimary && (
															<Star className='absolute -top-1 -right-1 w-3 h-3 text-yellow-500 fill-yellow-500' title='Primary image' />
														)}
													</div>
												))}
												{remainingCount > 0 && (
													<span className='text-sm text-gray-600 font-montserrat-medium'>
														+{remainingCount} more
													</span>
												)}
											</div>
										) : (
											<span className='text-sm font-montserrat-regular text-gray-400 italic'>
												No images uploaded
											</span>
										)}
									</div>
								</div>
							);
						})}
						<div className='pt-4 text-sm text-gray-600'>
							Total: {totalUploadedImages} of {totalPossibleSlots} photo slots filled ({uploadProgress}%)
						</div>
					</div>
				)}

				{/* Done button for profile editing mode */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							onClick={() => toggleCardEditMode(cardName)}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Done
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleOnboardingSave}
							disabled={!hasMinimumRequiredImages()}
							className={`w-full ${
								hasMinimumRequiredImages()
									? 'bg-gray-900 hover:bg-gray-800 text-white'
									: 'bg-gray-300 text-gray-500 cursor-not-allowed'
							}`}>
							<Check className='h-4 w-4 mr-2' />
							{hasMinimumRequiredImages() 
								? 'Save & Continue' 
								: 'Upload Required Images to Continue'}
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default OnboardingPhotosCard;